import bytes from 'bytes';
import { completeConfig } from '@/components/ui/data-table/config';
import { DataTable } from '@/components/ui/data-table/data-table';
import { TreeCellLeading } from '@/components/ui/data-table/TreeCellLeading';
import { Skeleton } from '@/components/ui/skeleton';
import { deserialize } from '@/components/ui/TokenizedInput/serialization';
import type { Token } from '@/components/ui/TokenizedInput/types';
import { docsTableConfig } from '@/config/table-configs';
import { usePanelFilterState } from '@/hooks/usePanelFilterState';
import { usePanelSelectionState } from '@/hooks/usePanelSelectionState';
import { usePersistedTableConfig } from '@/hooks/usePersistedTableConfig';
import { fieldsToTokenDefinitions } from '@/lib/filters/field-to-filter';
import { CatalogContentRow } from '@certhon/models';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, ExpandedState } from '@tanstack/react-table';
import { format } from 'date-fns';
import { File, FileText, Folder } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { docsFields } from './docs.fields';
import { docsQueries } from './docs.queries';
import {
  DocsFileNode,
  DocsGroupNode,
  DocsNode,
  getNodeLabel,
  getSubtree,
  revisionLabel,
} from './docs.tree';
import { useStackPath } from '@/app/navigation/useStackPath';
import { usePanelState } from '@/app/navigation/usePanelState';

interface Props {
  /** Project this catalog belongs to. */
  projectId: number;
  /**
   * The group whose subtree to render. null = catalog root (top-level groups).
   * For recursive group panels, pass the group panel's own id (which the
   * panel reads from `parentParams.groupOrFileId`).
   */
  groupId: number | null;
  /**
   * Base URL of the current panel. New panels are pushed by appending
   * `${recnum}/group` or `${recnum}/file`. The active-row highlight is
   * derived from the URL beyond this prefix (see useStackPath).
   */
  basePath: string;
}

/**
 * Docs catalog tree view. Renders the subtree rooted at `groupId` (or the
 * catalog root when null) as a DataTable in tree mode.
 *
 * Interaction model differs per viewport (see `tree.rowClickAction`):
 * - Desktop: clicking a group row (body or chevron) expands it in place.
 *   Groups have no separate drill target, so no disclosure button is shown
 *   (hasSecondaryDrill is left unset). File rows drill to the file panel on
 *   click.
 * - Mobile: clicking any row drills into the child panel; in-place
 *   expansion is available via the chevron.
 *
 * Uses the shared DataTable to inherit sorting, column reorder/visibility
 * config persistence, and multiselect. Filtering token-bar is wired but
 * starts with no token definitions; refining the tokens (has_file, type)
 * is a follow-up.
 */
export function DocsGroupTree({ projectId, groupId, basePath }: Props) {
  const { data: rows, isLoading } = useQuery(docsQueries.byProject(projectId));

  if (isLoading) {
    return <DocsGroupTreeSkeleton />;
  }
  if (!rows || rows.length === 0) {
    return (
      <p className="text-muted-foreground p-4 text-sm">
        No document catalog for this project.
      </p>
    );
  }

  return (
    <DocsGroupTreeInner rows={rows} groupId={groupId} basePath={basePath} />
  );
}

function DocsGroupTreeInner({
  rows,
  groupId,
  basePath,
}: {
  rows: CatalogContentRow[];
  groupId: number | null;
  basePath: string;
}) {
  const stackPath = useStackPath();
  // Active-row id derived from URL beyond this panel's basePath.
  // Expected suffix shape: `/{recnum}/{group|file}`.
  const activeRowId = useMemo(() => {
    if (!stackPath.startsWith(basePath)) return null;
    const suffix = stackPath.slice(basePath.length);
    const parts = suffix.split('/').filter(Boolean);
    if (parts.length < 2) return null;
    const [id, kind] = parts;
    if (kind !== 'group' && kind !== 'file') return null;
    return `${kind}:${id}`;
  }, [stackPath, basePath]);
  const subtree = useMemo(() => getSubtree(rows, groupId), [rows, groupId]);
  const catalogId = rows[0]?.catalog_id ?? 0;

  const { filterValue: rawFilterValue, setFilterValue } = usePanelFilterState({
    defaultFilter: 'has_file is: "true"',
  });
  // String-coerce defensively because url-param parsing may hand us a
  // number when the filter is purely numeric (e.g. "1.1") or a boolean for
  // values like "true". DataTable's TokenizedInput requires a string.
  const filterValue = String(rawFilterValue ?? '');
  const { rowSelection, setRowSelection } = usePanelSelectionState();

  // Build token definitions from docsFields. file_type options are
  // populated at runtime from the extensions actually present in this
  // catalog. Patch the generated token def directly because changeMeta on
  // a Zod schema doesn't reliably propagate option changes to the
  // generated token definition.
  const tokenDefinitions = useMemo(() => {
    const extensions = collectExtensions(rows);
    const defs = fieldsToTokenDefinitions(docsFields);
    return defs.map((td) =>
      td.key === 'file_type'
        ? {
            ...td,
            options: extensions.map((ext) => ({ label: ext, value: ext })),
          }
        : td,
    );
  }, [rows]);

  // Parse the current filter value into tokens + free-text remainder.
  const { tokens, remainder } = useMemo(
    () => deserialize(filterValue),
    [filterValue],
  );
  const isFiltering = tokens.length > 0 || !!remainder;

  // Prune the subtree according to the active tokens / remainder. Returns
  // the original subtree when no filter is active.
  const visibleSubtree = useMemo(() => {
    if (!isFiltering) return subtree;
    return pruneTree(subtree, tokens, remainder ?? '');
  }, [subtree, tokens, remainder, isFiltering]);

  const totalFiles = useMemo(() => countFiles(subtree), [subtree]);
  const visibleFiles = useMemo(
    () => countFiles(visibleSubtree),
    [visibleSubtree],
  );

  // Drill action: push a new group / file panel.
  const drillUrl = useCallback(
    (node: DocsNode) => {
      const suffix = node.kind === 'group' ? 'group' : 'file';
      const stripped = basePath.endsWith('/')
        ? basePath.slice(0, -1)
        : basePath;
      return `${stripped}/${node.recnum}/${suffix}`;
    },
    [basePath],
  );

  const columns = useMemo<ColumnDef<DocsNode>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (n) => getNodeLabel(n, 'en'),
        cell: ({ row }) => {
          const node = row.original;
          // Distinguish filetokens that have a current revision (FileText,
          // with content lines) from empty slots (plain File outline).
          const Icon =
            node.kind === 'group'
              ? Folder
              : node.file_recnum != null
                ? FileText
                : File;
          return (
            <TreeCellLeading row={row} icon={Icon}>
              {getNodeLabel(node, 'en')}
            </TreeCellLeading>
          );
        },
        enableSorting: true,
      },
      {
        id: 'revision',
        header: 'Revision',
        accessorFn: (n) => (n.kind === 'file' ? (n.revision_nr ?? -1) : null),
        cell: ({ row }) => {
          const node = row.original;
          if (node.kind === 'group') {
            return (
              <span className="text-muted-foreground text-xs">
                {countDescendants(node)} items
              </span>
            );
          }
          if (node.file_recnum == null) {
            return (
              <span className="text-muted-foreground text-xs">no file</span>
            );
          }
          return (
            <span className="text-xs">
              {revisionLabel(node.revision_nr ?? 0)}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        id: 'size',
        header: 'Size',
        accessorFn: (n) => (n.kind === 'file' ? (n.size ?? 0) : null),
        cell: ({ row }) => {
          const node = row.original;
          if (node.kind !== 'file' || node.size == null) return null;
          return (
            <span className="text-xs">
              {bytes(node.size, { decimalPlaces: 1, unitSeparator: ' ' })}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        id: 'date',
        header: 'Date',
        accessorFn: (n) => {
          const d = nodeLastDate(n);
          return d ? d.toISOString() : null;
        },
        cell: ({ row }) => {
          const node = row.original;
          const d = nodeLastDate(node);
          if (!d) return null;
          return <span className="text-xs">{format(d, 'yyyy-MM-dd')}</span>;
        },
        enableSorting: true,
      },
      {
        id: 'extension',
        header: 'Extension',
        accessorFn: (n) =>
          n.kind === 'file' ? (n.file_extension ?? null) : null,
        cell: ({ row }) => {
          const node = row.original;
          if (node.kind !== 'file' || !node.file_extension) return null;
          return <span className="text-xs">{node.file_extension}</span>;
        },
        enableSorting: true,
      },
      {
        id: 'checksum',
        header: 'Checksum',
        accessorFn: (n) => (n.kind === 'file' ? (n.checksum ?? null) : null),
        cell: ({ row }) => {
          const node = row.original;
          if (node.kind !== 'file' || !node.checksum) return null;
          return (
            <span className="text-muted-foreground font-mono text-xs">
              {node.checksum.slice(0, 8)}
            </span>
          );
        },
        enableSorting: true,
      },
    ],
    [],
  );

  const completeDefaultConfig = useMemo(
    () => completeConfig(docsTableConfig, columns),
    [columns],
  );

  // Persist table config keyed per-catalog so different catalogs don't share
  // expansion / column state.
  const [tableConfig, setTableConfig] = usePersistedTableConfig(
    `docs:${catalogId}`,
    completeDefaultConfig,
  );

  // Persist expansion state in URL via panel state so it survives
  // panel push/pop (mobile remounts the panel on every drill-in).
  // PanelState's value type is declared as string but the underlying router
  // round-trips arbitrary JSON values through the search param; the type is
  // overly narrow. Cast through unknown when reading/writing the structured
  // ExpandedState slot.
  const [pstate, setPState] = usePanelState();
  const expanded = (pstate.expanded ?? {}) as unknown as ExpandedState;
  const onExpandedChange = useCallback(
    (updater: ExpandedState | ((prev: ExpandedState) => ExpandedState)) => {
      setPState((prev) => ({
        ...prev,
        expanded: (typeof updater === 'function'
          ? updater((prev.expanded ?? {}) as unknown as ExpandedState)
          : updater) as unknown as string,
      }));
    },
    [setPState],
  );

  // Stable object literal so DataTable's memo isn't busted every render.
  // Desktop: expansion is the primary navigation model (row body + chevron
  // expand); groups expose no secondary drill, so hasSecondaryDrill is unset.
  // Mobile: drill-down stays primary, expansion via the chevron.
  const treeConfig = useMemo(
    () => ({
      getSubRows: (n: DocsNode) =>
        n.kind === 'group' ? (n.children as DocsNode[]) : undefined,
      expanded,
      onExpandedChange,
      rowClickAction: {
        desktop: 'expand' as const,
        mobile: 'drill' as const,
      },
    }),
    [expanded, onExpandedChange],
  );

  return (
    <DataTable<DocsNode, unknown>
      data={visibleSubtree}
      columns={columns}
      config={tableConfig}
      onConfigChange={setTableConfig}
      createUriPath={drillUrl}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      tokenDefinitions={tokenDefinitions}
      isFiltering={isFiltering}
      resultCount={visibleFiles}
      totalCount={totalFiles}
      entityName="docs"
      defaultFilter='has_file is: "true"'
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      getRowId={(n) => `${n.kind}:${n.recnum}`}
      activeRowId={activeRowId}
      tree={treeConfig}
    />
  );
}

// ---------------------------------------------------------------------------
// Tree filter
// ---------------------------------------------------------------------------
//
// File-only tokens (has_file, file_type) only match files; groups stay
// visible if any descendant file matches all tokens. The `name` token and
// the free-text remainder match against either a node's own label OR any
// descendant's label (i.e. they also keep ancestor groups visible).
//
// pruneTree returns a new tree with non-matching branches dropped.

function pruneTree(
  nodes: DocsNode[],
  tokens: Token[],
  remainder: string,
): DocsNode[] {
  const trimmedRemainder = remainder.trim().toLowerCase();
  const result: DocsNode[] = [];
  for (const node of nodes) {
    if (node.kind === 'file') {
      if (matchesFile(node, tokens, trimmedRemainder)) {
        result.push(node);
      }
    } else {
      const prunedChildren = pruneTree(node.children, tokens, trimmedRemainder);
      const nameTokensApply = tokens.every((t) =>
        t.field === 'name'
          ? matchesNameToken(getNodeLabel(node, 'en'), t)
          : true,
      );
      const groupSelfTextMatches =
        !trimmedRemainder ||
        getNodeLabel(node, 'en').toLowerCase().includes(trimmedRemainder);
      // Group visible if:
      //  - has visible descendants, OR
      //  - the group itself satisfies the name + text predicates AND no
      //    file-only tokens are active (otherwise the user is explicitly
      //    filtering for files; empty groups shouldn't show).
      const onlyTextOrNameFilters = tokens.every((t) => t.field === 'name');
      const selfMatches =
        nameTokensApply && groupSelfTextMatches && onlyTextOrNameFilters;
      if (prunedChildren.length > 0 || selfMatches) {
        result.push({ ...node, children: prunedChildren });
      }
    }
  }
  return result;
}

function matchesFile(
  file: DocsFileNode,
  tokens: Token[],
  textRemainder: string,
): boolean {
  for (const t of tokens) {
    if (t.field === 'name') {
      if (!matchesNameToken(getNodeLabel(file, 'en'), t)) return false;
    } else if (t.field === 'has_file') {
      const hasFile = file.file_recnum != null;
      const want = t.value.toLowerCase() === 'true';
      // 'is' (default) → equality; 'is_not' → inequality.
      const ok = t.operation === 'is_not' ? hasFile !== want : hasFile === want;
      if (!ok) return false;
    } else if (t.field === 'file_type') {
      const ext = file.file_extension?.replace(/^\./, '').toLowerCase();
      if (!ext) return false;
      const want = t.value.replace(/^\./, '').toLowerCase();
      const ok = t.operation === 'is_not' ? ext !== want : ext === want;
      if (!ok) return false;
    } else if (t.field === 'date') {
      if (!matchesDateToken(file.date_in ?? null, t)) return false;
    }
  }
  if (textRemainder) {
    if (!getNodeLabel(file, 'en').toLowerCase().includes(textRemainder)) {
      return false;
    }
  }
  return true;
}

/**
 * Match a date value against a date-typed token. Token value is expected to
 * be an ISO-ish date (YYYY-MM-DD). Comparison is done in calendar-day
 * granularity. null dates fail any comparison.
 */
function matchesDateToken(value: Date | null, token: Token): boolean {
  if (!value) return false;
  const want = parseDateLoose(token.value);
  if (!want) return true; // ignore malformed input rather than hide everything
  const vDay = startOfDay(value).getTime();
  const wDay = startOfDay(want).getTime();
  switch (token.operation) {
    case 'is':
      return vDay === wDay;
    case 'is_not':
      return vDay !== wDay;
    case 'gt':
      return vDay > wDay;
    case 'gte':
      return vDay >= wDay;
    case 'lt':
      return vDay < wDay;
    case 'lte':
      return vDay <= wDay;
    default:
      return vDay === wDay;
  }
}

function parseDateLoose(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function matchesNameToken(label: string, token: Token): boolean {
  const v = token.value.toLowerCase();
  const lbl = label.toLowerCase();
  switch (token.operation) {
    case 'is':
      return lbl === v;
    case 'is_not':
      return lbl !== v;
    case 'contains':
      return lbl.includes(v);
    case 'contains_not':
      return !lbl.includes(v);
    default:
      return lbl.includes(v);
  }
}

function nodeLastDate(node: DocsNode): Date | null {
  if (node.kind === 'file') return node.date_in ?? null;
  return node.last_date;
}

function countFiles(nodes: DocsNode[]): number {
  let n = 0;
  for (const node of nodes) {
    if (node.kind === 'file') n++;
    else n += countFiles(node.children);
  }
  return n;
}

function collectExtensions(rows: CatalogContentRow[]): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    if (r.kind !== 'file' || !r.file_extension) continue;
    const ext = r.file_extension.replace(/^\./, '').toLowerCase();
    if (ext) set.add(ext);
  }
  return Array.from(set).sort();
}

function DocsGroupTreeSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-7 w-full" />
      ))}
    </div>
  );
}

function countDescendants(group: DocsGroupNode): number {
  let count = 0;
  for (const child of group.children) {
    count++;
    if (child.kind === 'group') count += countDescendants(child);
  }
  return count;
}

// Silence unused-import linter for types we don't reference inline.
export type { DocsFileNode };
